import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithHttpStatus } from '../services/baseQuery';
// START-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
import { CompanyFieldOptionsResponse, RemoveCompanyRequest, RemoveCompanyResponse } from '../../interfaces/management/company-management.interface';
// /END-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
import { Badge, GetBadgesParams, GetUserBadgesParams } from '../../interfaces/badges.interface';

// Define a service using a base URL and expected endpoints
export const adminStore = createApi({
    reducerPath: 'adminStore',
    baseQuery: baseQueryWithHttpStatus,
    // START-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
    tagTypes: ['Badge', 'CompanyFieldOptions', 'Company'],
    // /END-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
    endpoints: (builder) => ({
        getCompaniesList: builder.mutation({
            query: (body) => {
                return {
                    url: '/gptBlueAdmin/getCompaniesList/',
                    method: 'POST',
                    body: { ...body },
                }
            },
        }),
        getBadges: builder.query<{ badges: Badge[] }, GetBadgesParams>({
              query: (params) => {
                return {
                    url: "/gptBlueAdmin/getBadges/",
                    method:"GET",
                    params: params
                }
            },
            providesTags: ['Badge'],
        }),
        getUserBadges: builder.query<{ badges: Badge[] }, GetUserBadgesParams>({
            query: (params) => {
                return {
                url: "/userManagerCFC/getUserBadges/",
                method: "GET",
                params: params
                }
            },
            providesTags: ['Badge']
        }),
        manageBadges: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/manageBadges/",
                    method:"POST",
                    body: {...body}
                }
            },
            invalidatesTags: ['Badge'],
        }),
        assignUserBadges:  builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/gptBlueManageUserBadge",
                    method:"POST",
                    body: {...body}
                }
            },
            invalidatesTags: ['Badge'],
        }),
        manageTranslations: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/manageTranslation/",
                    method:"POST",
                    body: {...body}
                }
            },
        }),
        RequestLibraryApproval: builder.mutation({
            query: (body) => {
                return {
                    url: 'gptBlueAdmin/getLibraryForApproval/',
                    method: 'POST',
                    body: {...body}
                }
            },
        }),
        LibApprovalAction:  builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/libraryRequestReview/",
                    method:"POST",
                    body: {...body}
                }
            },
        }),
        getPayoutRequestToProcess: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/getPayoutRequestToProcess/",
                    method:"POST",
                    body: {...body}
                }
            },
        }),
        processPayoutRequest: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/processPayoutRequest/",
                    method:"POST",
                    body: {...body}
                }
            },
        }),
        addReward: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/addRewardCoin/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        getCompanyDetails: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/getCompanyDetails/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        getCommunicationToUser: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/getCommunicationToUser/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        // START-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
        getCompanyFieldOptions: builder.query<CompanyFieldOptionsResponse, void>({
            query: () => {
                return {
                url: "/gptBlueSystemCFC/getCompanyFieldOptions/",
                method: "GET"
                }
            },
            providesTags: ['CompanyFieldOptions'],
        }),
        // /END-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
        updateCommunicationToUser: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/updateCommunicationToUser/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        sendCommunicationToUser: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/sendCommunicationToUser/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        getUpgradeRequest: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/getUpgradeRequest/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        updateUpgradeRequest: builder.mutation({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/updateUpgradeRequest/",
                    method: "POST",
                    body: {...body}
                }
            },
        }),
        // START-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
        removeCompany: builder.mutation<RemoveCompanyResponse, RemoveCompanyRequest>({
            query: (body) => {
                return {
                    url: "/gptBlueAdmin/removeCompany/",
                    method: "DELETE",
                    body: {...body}
                }
            },
            invalidatesTags: ['Company'],
        }),
        // /END-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
    }),
})

export const {
    useGetCompaniesListMutation,
    useGetBadgesQuery,
    useGetUserBadgesQuery,
    useManageBadgesMutation,
    useAssignUserBadgesMutation,
    useManageTranslationsMutation,
    useRequestLibraryApprovalMutation,
    useLibApprovalActionMutation,
    useGetPayoutRequestToProcessMutation,
    useProcessPayoutRequestMutation,
    useAddRewardMutation,
    useGetCompanyDetailsMutation,
    useGetCommunicationToUserMutation,
    useUpdateCommunicationToUserMutation,
    useSendCommunicationToUserMutation,
    useGetUpgradeRequestMutation,
    useUpdateUpgradeRequestMutation,
    // START-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
    useRemoveCompanyMutation,
    useGetCompanyFieldOptionsQuery,
    useLazyGetCompanyFieldOptionsQuery,
    // /END-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
} = adminStore;
